import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Space, Switch, Table, Tag, Tooltip, message } from 'antd';
import type { TableProps } from 'antd';
import { SwapOutlined, KeyOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  supabase,
  provisionAuthUser,
  usePortalPermissions,
  resetPasswordRedirectUrl,
  formatDateTime,
  invokeEdgeFunction,
  SetupCredentialsNotice,
  useAuthUser,
  parseUserRole,
  isAdminRole,
  sendPasswordSetupLink,
  logAssistResetAudit,
} from '@ubus/shared';
import type { FleetOperator } from '@ubus/shared';
import StatusTag from '../components/StatusTag';
import { useAdminTableStability } from '../hooks/useAdminTableStability';
import { localeConfirm, localeSuccess } from '../utils/localeModal';

export interface FleetTeamMember {
  auth_uid: string;
  email: string;
  display_name: string | null;
  status: string;
  role_id: string | null;
  role_code: string | null;
  role_name: string | null;
  is_primary: boolean;
  last_sign_in_at: string | null;
  created_at: string | null;
}

export interface FleetOperatorMembersPanelProps {
  operator: FleetOperator;
  /** Re-fetch the parent operator row (e.g. after provisioning/transfer). */
  onOperatorChanged?: () => void;
}

function FleetOperatorMembersPanel({
  operator,
  onOperatorChanged,
}: FleetOperatorMembersPanelProps) {
  const { t } = useTranslation();
  const { hasPermission } = usePortalPermissions();
  const authUser = useAuthUser();
  const callerIsAdmin = isAdminRole(parseUserRole(authUser));
  const canView =
    hasPermission('fleet_operators.members.view') ||
    hasPermission('fleet_operators.view');
  const canManage = hasPermission('fleet_operators.members.disable');
  // Transfer primary is a platform-only repair action: requires members.disable
  // AND an admin identity. A platform non-admin with members.disable must not
  // see the button (the fleet's own primary handles self-transfer).
  const canTransferPrimary = canManage && callerIsAdmin;
  const canProvision = hasPermission('fleet_operators.provision');
  // Platform operators who can open this panel may reset the fleet super user.
  // (Edge send-password-setup still requires admin identity.)
  const canResetPrimaryPassword =
    canProvision ||
    canManage ||
    hasPermission('fleet_operators.members.view') ||
    hasPermission('fleet_operators.view') ||
    hasPermission('system.users.manage');

  const isFleetSuperUser = (row: FleetTeamMember) =>
    row.is_primary ||
    (Boolean(operator.primary_manager_uid) &&
      row.auth_uid === operator.primary_manager_uid) ||
    (Boolean(operator.auth_uid) && row.auth_uid === operator.auth_uid);
  const { pagination, onTableChange, rowClassName, afterMutation } =
    useAdminTableStability();

  const [members, setMembers] = useState<FleetTeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [togglingUid, setTogglingUid] = useState<string | null>(null);
  const [transferringUid, setTransferringUid] = useState<string | null>(null);

  const hasPrimary =
    Boolean(operator.primary_manager_uid) || Boolean(operator.auth_uid);

  const fetchMembers = useCallback(async () => {
    if (!canView) {
      setMembers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('list_fleet_team_members', {
        p_fleet_operator_id: operator.id,
      });
      if (error) {
        message.error(error.message);
        setMembers([]);
      } else {
        setMembers((data ?? []) as FleetTeamMember[]);
      }
    } finally {
      setLoading(false);
    }
  }, [operator.id, canView]);

  useEffect(() => {
    void fetchMembers();
  }, [fetchMembers]);

  const handleStatusToggle = async (row: FleetTeamMember, active: boolean) => {
    const nextStatus = active ? 'active' : 'disabled';
    setTogglingUid(row.auth_uid);
    try {
      const { error } = await supabase.rpc('set_fleet_member_status', {
        p_auth_uid: row.auth_uid,
        p_status: nextStatus,
      });
      if (error) throw error;

      // Sync auth ban via provision-user edge function.
      const { error: efError } = await invokeEdgeFunction('provision-user', {
        body: {
          action: 'set_status',
          user_id: row.auth_uid,
          status: nextStatus,
        },
      });
      if (efError) {
        // Split-brain guard: the profile was just disabled but the auth ban
        // failed. Roll the profile back to active so the user is not left in
        // a state where the profile says disabled but the identity can still
        // sign in. Only roll back when we disabled (not when we enabled).
        if (nextStatus === 'disabled') {
          const { error: rollbackError } = await supabase.rpc(
            'set_fleet_member_status',
            { p_auth_uid: row.auth_uid, p_status: 'active' }
          );
          if (rollbackError) {
            message.warning(
              `${efError.message} — ${t('fleetOperators.members.rollbackFailed')}`
            );
          } else {
            message.warning(
              t('fleetOperators.members.disabledRolledBack', {
                defaultValue: 'Auth ban failed; profile rolled back to active.',
              })
            );
          }
        } else {
          message.warning(efError.message);
        }
      }

      message.success(t('fleetOperators.members.statusUpdated'));
      await afterMutation(row.auth_uid, fetchMembers);
    } catch (e) {
      message.error(e instanceof Error ? e.message : t('fleetOperators.members.updateFailed'));
      await fetchMembers();
    } finally {
      setTogglingUid(null);
    }
  };

  const handleTransferPrimary = (row: FleetTeamMember) => {
    localeConfirm(t, {
      title: t('fleetOperators.members.transferTitle'),
      content: t('fleetOperators.members.transferContent', {
        name: row.display_name || row.email,
      }),
      okText: t('fleetOperators.members.transfer'),
      okType: 'danger',
      onOk: async () => {
        setTransferringUid(row.auth_uid);
        try {
          const { error } = await supabase.rpc('transfer_fleet_primary_manager', {
            p_fleet_operator_id: operator.id,
            p_new_primary_uid: row.auth_uid,
          });
          if (error) throw error;
          message.success(t('fleetOperators.members.transferred'));
          await fetchMembers();
          onOperatorChanged?.();
        } catch (e) {
          message.error(
            e instanceof Error ? e.message : t('fleetOperators.members.transferFailed')
          );
        } finally {
          setTransferringUid(null);
        }
      },
    });
  };

  const handleProvisionLogin = () => {
    localeConfirm(t, {
      title: t('fleetOperators.messages.provisionTitle'),
      content: t('fleetOperators.messages.provisionContent', {
        email: operator.contact_email,
      }),
      okText: t('fleetOperators.messages.provision'),
      onOk: async () => {
        try {
          const auth = await provisionAuthUser({
            role: 'fleet_owner',
            email: operator.contact_email,
            fleetOperatorId: operator.id,
            portalRoleCode: 'fleet_manager',
            redirectTo: resetPasswordRedirectUrl(),
          });
          localeSuccess(t, {
            title: t('fleetOperators.messages.accountReady'),
            width: 560,
            content: (
              <SetupCredentialsNotice email={auth.email} setupLink={auth.setupLink} />
            ),
          });
          await fetchMembers();
          onOperatorChanged?.();
        } catch (err) {
          message.error(
            err instanceof Error ? err.message : t('fleetOperators.messages.provisionFailed')
          );
        }
      },
    });
  };

  const handleResetPrimaryPassword = (row: FleetTeamMember) => {
    if (!canResetPrimaryPassword || !isFleetSuperUser(row)) {
      message.warning(t('fleetOperators.members.resetPrimaryOnly'));
      return;
    }
    const label = row.display_name || row.email;
    localeConfirm(t, {
      title: t('rbac.sendSetupLink'),
      content: t('rbac.setupLinkConfirm', { name: label }),
      okText: t('rbac.sendSetupLink'),
      onOk: async () => {
        try {
          const result = await sendPasswordSetupLink({
            email: row.email,
            userId: row.auth_uid,
            redirectTo: resetPasswordRedirectUrl(),
            sendEmail: true,
          });
          logAssistResetAudit({
            actorId: authUser?.id,
            targetType: 'fleet_primary',
            targetId: row.auth_uid,
            channel: result.emailed ? 'email_link' : 'link_only',
            emailed: result.emailed,
          });
          localeSuccess(t, {
            title: t('rbac.setupLinkSent'),
            width: 560,
            content: (
              <SetupCredentialsNotice
                email={result.email || row.email}
                setupLink={result.setupLink}
                extraNote={
                  result.emailed
                    ? t('rbac.setupLinkEmailed', {
                        email: result.recipient || row.email,
                      })
                    : t('rbac.setupLinkShareHint')
                }
              />
            ),
          });
        } catch (err) {
          message.error(
            err instanceof Error ? err.message : t('rbac.setupLinkFailed')
          );
        }
      },
    });
  };

  const columns: TableProps<FleetTeamMember>['columns'] = [
    {
      title: t('fleetOperators.members.columns.name'),
      key: 'name',
      width: 200,
      ellipsis: true,
      render: (_, r) => (
        <Space size={6} wrap={false}>
          <span>{r.display_name || r.email.split('@')[0]}</span>
          {r.is_primary || isFleetSuperUser(r) ? (
            <Tag color="gold">{t('fleetOperators.members.primaryTag')}</Tag>
          ) : null}
        </Space>
      ),
    },
    {
      title: t('fleetOperators.columns.email'),
      dataIndex: 'email',
      key: 'email',
      width: 220,
      ellipsis: true,
    },
    {
      title: t('fleetOperators.members.columns.role'),
      key: 'role',
      width: 160,
      ellipsis: true,
      render: (_, r) => r.role_name ?? r.role_code ?? '—',
    },
    {
      title: t('fleetOperators.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: string) => <StatusTag status={status} />,
    },
    {
      title: t('fleetOperators.members.columns.lastSignIn'),
      dataIndex: 'last_sign_in_at',
      key: 'last_sign_in_at',
      width: 160,
      render: (v: string | null) => (v ? formatDateTime(v) : '—'),
    },
    {
      title: t('fleetOperators.columns.actions'),
      key: 'actions',
      width: 280,
      fixed: 'right',
      render: (_, r) => (
        <Space size={8} wrap>
          {canResetPrimaryPassword && isFleetSuperUser(r) ? (
            <Button
              type="link"
              size="small"
              icon={<KeyOutlined />}
              onClick={() => handleResetPrimaryPassword(r)}
            >
              {t('rbac.sendSetupLink')}
            </Button>
          ) : null}
          {canManage ? (
            <Tooltip
              title={
                r.is_primary
                  ? t('fleetOperators.members.cannotDisablePrimary')
                  : undefined
              }
            >
              <Switch
                checked={r.status === 'active'}
                loading={togglingUid === r.auth_uid}
                disabled={r.is_primary}
                onChange={(checked) => void handleStatusToggle(r, checked)}
                checkedChildren={t('fleetOperators.members.active')}
                unCheckedChildren={t('fleetOperators.members.disabled')}
              />
            </Tooltip>
          ) : null}
          {canManage && !r.is_primary && canTransferPrimary ? (
            <Button
              type="link"
              size="small"
              icon={<SwapOutlined />}
              loading={transferringUid === r.auth_uid}
              onClick={() => handleTransferPrimary(r)}
            >
              {t('fleetOperators.members.transfer')}
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  if (!canView) {
    return (
      <Alert
        type="warning"
        showIcon
        message={t('fleetOperators.members.noViewPermission')}
      />
    );
  }

  return (
    <div>
      {!hasPrimary ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('fleetOperators.members.noPrimary')}
          description={
            canProvision ? (
              <Button
                type="primary"
                icon={<KeyOutlined />}
                onClick={handleProvisionLogin}
                style={{ marginTop: 8 }}
              >
                {t('fleetOperators.actions.provision')}
              </Button>
            ) : undefined
          }
        />
      ) : null}

      <Table
        rowKey="auth_uid"
        loading={loading}
        columns={columns}
        dataSource={members}
        pagination={pagination}
        onChange={onTableChange}
        rowClassName={rowClassName}
        scroll={{ x: 900 }}
        locale={{ emptyText: t('fleetOperators.members.empty') }}
      />
    </div>
  );
}

export default FleetOperatorMembersPanel;
